import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { cn } from '@/shared/utils/cn';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/shared/ui/sidebar';
import { useCurrentUser } from './useCurrentUser';
import { UserMenuPopup } from './UserMenuPopup';

type NavigationUserDetails = {
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
};

const triggerCollapsedStyle: React.CSSProperties = { borderRadius: 8 };
const triggerExpandedStyle: React.CSSProperties = {
  padding: 8,
  borderRadius: 8,
  gap: 8,
};

const triggerNameStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '22px',
  color: '#334155',
};

const chevronWrapperStyle: React.CSSProperties = { width: 18, height: 18 };
const chevronIconStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  color: '#94A3B8',
};

function getInitials(user: NavigationUserDetails | undefined): string {
  if (!user) return '';
  const first = user.firstName?.[0] ?? '';
  const last = user.lastName?.[0] ?? '';
  const initials = `${first}${last}`.trim();
  if (initials) return initials.toUpperCase();
  return user.name?.[0]?.toUpperCase() ?? '';
}

const toggleOpen = (prev: boolean) => !prev;

export function UserMenu() {
  const user = useCurrentUser() as
    | (ReturnType<typeof useCurrentUser> & NavigationUserDetails)
    | undefined;
  const { state: sidebarState, isMobile } = useSidebar();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleTriggerClick = useCallback(() => setOpen(toggleOpen), []);
  const handlePopupClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (!user) return null;

  const displayName =
    `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
    user.name ||
    '';

  const triggerName = user.firstName || displayName.split(' ')[0] || displayName;

  // The mobile Sheet always renders the expanded UserMenu — the desktop
  // `state` (cookie-driven) has no meaning there. Prevents the collapsed
  // icon-only layout from leaking into mobile when the desktop sidebar
  // was previously collapsed.
  const isCollapsed = !isMobile && sidebarState === 'collapsed';

  return (
    <SidebarMenu className="gap-0">
      <SidebarMenuItem className="relative">
        <SidebarMenuButton
          ref={triggerRef}
          size="lg"
          onClick={handleTriggerClick}
          className={cn(
            '!h-auto hover:bg-black/5',
            open && 'bg-black/5',
            isCollapsed && '!size-10 !p-0 justify-center',
          )}
          style={isCollapsed ? triggerCollapsedStyle : triggerExpandedStyle}
        >
          <Avatar className="size-[40px] shrink-0 rounded-lg">
            {user.avatarUrl && (
              <AvatarImage src={user.avatarUrl} alt={displayName} />
            )}
            <AvatarFallback className="rounded-lg bg-[#EDEDED]">
              {getInitials(user)}
            </AvatarFallback>
          </Avatar>
          {!isCollapsed && (
            <>
              <div className="flex flex-1 flex-col items-start justify-center min-w-0">
                <span className="truncate w-full text-left" style={triggerNameStyle}>{triggerName}</span>
                {user.email && (
                  <span className="truncate w-full text-left font-normal text-[13px] leading-[18px] text-[#64748B]">
                    {user.email}
                  </span>
                )}
              </div>
              <span className="shrink-0 flex" style={chevronWrapperStyle}>
                <ChevronsUpDown style={chevronIconStyle} />
              </span>
            </>
          )}
        </SidebarMenuButton>

        {open && (
          <div ref={menuRef}>
            <UserMenuPopup
              displayName={displayName}
              email={user.email}
              onClose={handlePopupClose}
              className={cn(
                'absolute',
                isCollapsed
                  ? 'bottom-0 left-full ml-2 w-[246px]'
                  : 'bottom-[60px] left-[8px] right-[8px]',
              )}
            />
          </div>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
